import { useState, useEffect, useRef, useContext } from 'react'
import { MarkupContext } from '../../providers/Markup'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import themeConfig from '../../../theme.config'
import { dataLayerPush } from '../../helpers/thirdparty'

// ─────────────────────────────────────────────────────────────────────────────
// DESKTOP ▸ DesktopNavSection
// Renders a single L1 item inside Pane 1.
// Mirrors MegaMenuSection: link row on left, optional ▶ toggle on right.
// ─────────────────────────────────────────────────────────────────────────────
const DesktopNavSection = ({
  item, // nav child: { label, url, target, children[], disablePageLinkInMegaMenu }
  colIdx, // parent column index — used to build unique DOM IDs
  sIdx, // section index within the column
  active, // is the parent column panel open?
  isExpanded, // is THIS section's L2 pane showing?
  onExpandToggle,
  onLinkKeyDown,
  onToggleKeyDown,
  category,
}) => {
  const hasL2 = item.children?.filter((c) => !c.disablePageLinkInMegaMenu).length > 0

  return (
    <div className="mega-menu-section group relative">
      {/* Full-row hover on the container — identical appearance whether or not
          a ▶ toggle button is present (fixes the "borderline vs background" split) */}
      <div className="flex items-center justify-between rounded-lg transition-colors hover:bg-(--color-button-text-hover-bg)">
        {/* L1 link (or non-linking label) */}
        {!item.disablePageLinkInMegaMenu ? (
          <a
            id={`main-l1-${colIdx}-${sIdx}-link`}
            href={item.url}
            target={item.target ?? '_self'}
            className="flex items-start gap-3 p-3 flex-1 rounded-lg"
            tabIndex={active ? 0 : -1}
            onKeyDown={onLinkKeyDown}
            onClick={() => {
              dataLayerPush({
                event: 'navigation_menu_click',
                category: category,
                click_text: item.label,
              })
            }}
          >
            <span className="text-base font-bold !text-(--sb-mega-menu-title-color) leading-6">{item.label}</span>
          </a>
        ) : (
          /* No page link — act as a section header / expand button */
          <button
            id={`main-l1-${colIdx}-${sIdx}-link`}
            type="button"
            onClick={(e) => {
              e.preventDefault()
              onExpandToggle()
              dataLayerPush({
                event: 'navigation_menu_click',
                category: category,
                click_text: item.label,
              })
            }}
            className="flex items-start gap-3 p-3 flex-1 text-left rounded-lg"
            tabIndex={active ? 0 : -1}
            onKeyDown={onLinkKeyDown}
          >
            <span className="text-base font-bold !text-(--sb-mega-menu-title-color) leading-6">{item.label}</span>
          </button>
        )}

        {/* Expand chevron (only when L2 links exist).
            When expanded: persistent background highlight on just the button.
            Hover on the full row is handled by the container above. */}
        {hasL2 && (
          <button
            id={`main-l1-${colIdx}-${sIdx}-toggle`}
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onExpandToggle()
            }}
            onKeyDown={onToggleKeyDown}
            className={`p-3 shrink-0 ml-2 text-(--sb-mega-menu-chevron-down-icon-color) cursor-pointer rounded-lg ${
              isExpanded ? 'bg-(--color-button-text-hover-bg)' : ''
            }`}
            aria-expanded={isExpanded}
            aria-controls={`main-l2-pane-${colIdx}-${sIdx}`}
          >
            <span className="sr-only">Expand submenu for {item.label}</span>
            {/* Always pointing right — L2 opens in Pane 2, not below */}
            <ChevronDownIcon className="h-4 w-4 -rotate-90" />
          </button>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// DESKTOP ▸ DesktopNavColumn
// A full top-level nav item with a three-pane mega menu panel.
// Mirrors MegaMenuColumn exactly:
//   Pane 1 (320px) — L1 sections list
//   Pane 2 (320px) — L2 links of the active L1 section
//   Pane 3 (260px) — CTA image (navItem.image), themed background
// ─────────────────────────────────────────────────────────────────────────────
const DesktopNavColumn = ({ navItem, colIdx, isOpen, onToggle }) => {
  const { detectPaletteClass } = useContext(MarkupContext)
  const themeHighlight = themeConfig.settings.nav?.desktop?.highlight || 'primary'
  const [activeSectionIndex, setActiveSectionIndex] = useState(null)
  const decorationColor = `--color-${themeHighlight}-500`

  // visible L1 items (sections that have a link or label)
  const sections = navItem.children || []
  const hasChildLinks = sections.length > 0

  // Reset expanded section when the panel closes
  useEffect(() => {
    setActiveSectionIndex(null)
  }, [isOpen])

  const handleSectionExpandToggle = (sIdx) => {
    setActiveSectionIndex((prev) => (prev === sIdx ? null : sIdx))
  }

  // ── WCAG Focus Routing ── (mirrors MegaMenuColumn logic) ─────────────────
  const handleL1LinkKeyDown = (e, sIdx) => {
    const hasL2 = sections[sIdx].children?.filter((c) => !c.disablePageLinkInMegaMenu).length > 0

    // BACKWARD: if the item above was expanded, dive into the last of its L2 links
    if (e.key === 'Tab' && e.shiftKey && sIdx > 0) {
      const prevIdx = sIdx - 1
      const prevL2 = sections[prevIdx].children?.filter((c) => !c.disablePageLinkInMegaMenu)
      if (activeSectionIndex === prevIdx && prevL2?.length > 0) {
        e.preventDefault()
        const el = document.getElementById(`main-l2-${colIdx}-${prevIdx}-${prevL2.length - 1}`)
        if (el) el.focus()
      }
    }

    // FORWARD: if this item has no chevron, skip past the (invisible) Pane 2 trap
    if (e.key === 'Tab' && !e.shiftKey && !hasL2) {
      e.preventDefault()
      if (sIdx < sections.length - 1) {
        const el = document.getElementById(`main-l1-${colIdx}-${sIdx + 1}-link`)
        if (el) el.focus()
      } else {
        const cta = document.getElementById(`main-cta-${colIdx}`)
        if (cta) cta.focus()
        else {
          onToggle(colIdx)
        }
      }
    }
  }

  const handleL1ToggleKeyDown = (e, sIdx) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      const isExpanded = activeSectionIndex === sIdx
      e.preventDefault()
      if (isExpanded) {
        // FORWARD (open): dive into the first L2 link
        const el = document.getElementById(`main-l2-${colIdx}-${sIdx}-0`)
        if (el) el.focus()
      } else {
        // FORWARD (closed): skip to next L1 item
        if (sIdx < sections.length - 1) {
          const el = document.getElementById(`main-l1-${colIdx}-${sIdx + 1}-link`)
          if (el) el.focus()
        } else {
          const cta = document.getElementById(`main-cta-${colIdx}`)
          if (cta) cta.focus()
        }
      }
    }
  }

  const handleCTAKeyDown = (e) => {
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault()
      if (activeSectionIndex !== null) {
        const l2Items = sections[activeSectionIndex]?.children?.filter((c) => !c.disablePageLinkInMegaMenu)
        if (l2Items?.length > 0) {
          const el = document.getElementById(`main-l2-${colIdx}-${activeSectionIndex}-${l2Items.length - 1}`)
          if (el) {
            el.focus()
            return
          }
        }
      }
      const lastL1 = sections.length - 1
      const toggle = document.getElementById(`main-l1-${colIdx}-${lastL1}-toggle`)
      if (toggle) toggle.focus()
      else {
        const link = document.getElementById(`main-l1-${colIdx}-${lastL1}-link`)
        if (link) link.focus()
      }
    }
  }

  // Active underline style (mirrors MegaMenuColumn classForOpenStatus)
  const classForOpenStatus = isOpen ? 'font-bold underline decoration-4 decoration-(--nav-decor)' : 'font-normal'

  return (
    <li
      data-level="0"
      className={`${isOpen ? 'active' : 'inactive'}`}
      onBlur={(e) => {
        if (isOpen && e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) {
          onToggle(colIdx)
        }
      }}
    >
      {/* ── Top-level link + chevron button ─────────────────────────────── */}
      <div className="flex items-center h-full">
        {!navItem.disablePageLinkInMegaMenu ? (
          <>
            <a
              id={`main-top-link-${colIdx}`}
              href={navItem.url || '#'}
              target={navItem.target ?? '_self'}
              style={{ '--nav-decor': `var(${decorationColor}, var(--sb-nav-decor-color))` }}
              className={detectPaletteClass(`
                h-full xl:px-1.5 2xl:px-3 py-3 flex items-center gap-2
                whitespace-nowrap
                !text-(--sb-mega-menu-title-color)
                hover:underline hover:decoration-4 hover:decoration-(--nav-decor)  
                focus:-outline-offset-4
                ${classForOpenStatus}
              `)}
              aria-haspopup={hasChildLinks ? 'true' : 'false'}
              aria-controls={hasChildLinks ? `main-megamenu-col-${colIdx}` : undefined}
              aria-expanded={hasChildLinks ? (isOpen ? 'true' : 'false') : undefined}
              onClick={() => {
                dataLayerPush({
                  event: 'navigation_menu_click',
                  category: navItem.label,
                  click_text: navItem.label,
                })
              }}
            >
              <span>{navItem.label}</span>
            </a>

            {hasChildLinks && (
              <button
                type="button"
                id={`main-top-btn-${colIdx}`}
                onClick={() => onToggle(colIdx)}
                className="xl:pr-2 2xl:pr-3 py-3 h-full flex items-center hover:scale-200 transition-all duration-300 scale-100"
              >
                <span className="sr-only">Expand menu for {navItem.label}</span>
                <ChevronDownIcon
                  className={`h-4 w-4 transition-transform text-(--sb-mega-menu-chevron-down-icon-color) duration-300 ${isOpen ? 'rotate-180' : ''}`}
                />
              </button>
            )}
          </>
        ) : (
          /* No page link: whole row is the toggle */
          <button
            type="button"
            id={`main-top-btn-${colIdx}`}
            onClick={() => onToggle(colIdx)}
            style={{ '--nav-decor': `var(${decorationColor}, var(--sb-nav-decor-color))` }}
            className={`h-full xl:px-1.5 2xl:px-3 py-3 hover:scale-200 transition-all duration-300 scale-100 flex items-center gap-2 whitespace-nowrap text-(--sb-mega-menu-title-color)! hover:underline hover:decoration-4 hover:decoration-(--nav-decor)   focus:-outline-offset-4 ${classForOpenStatus}`}
            aria-haspopup="true"
            aria-controls={`main-megamenu-col-${colIdx}`}
            aria-expanded={isOpen ? 'true' : 'false'}
          >
            <span>{navItem.label}</span>
            <ChevronDownIcon
              className={`h-4 w-4 transition-transform duration-300 text-(--sb-mega-menu-chevron-down-icon-color) ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>

      {/* ── Three-pane Mega Menu Panel ───────────────────────────────────── */}
      <div
        id={`main-megamenu-col-${colIdx}`}
        className={`
          ${isOpen ? 'flex' : 'hidden'}
          absolute top-full left-0
          min-w-[900px]
          bg-white
          shadow-xl ring-1 ring-black/5
          z-50
        `}
        onMouseLeave={() => {
          if (isOpen) onToggle(colIdx)
        }}
      >
        {/* ── PANE 1: L1 sections ── fixed 320px, white bg ─────────────── */}
        <div className="flex-none px-6 py-6" style={{ width: '320px' }}>
          <div className="flex flex-col space-y-1">
            {sections.map((child, sIdx) => (
              <DesktopNavSection
                key={`section-${sIdx}`}
                item={child}
                colIdx={colIdx}
                sIdx={sIdx}
                active={isOpen}
                isExpanded={activeSectionIndex === sIdx}
                onExpandToggle={() => handleSectionExpandToggle(sIdx)}
                onLinkKeyDown={(e) => handleL1LinkKeyDown(e, sIdx)}
                onToggleKeyDown={(e) => handleL1ToggleKeyDown(e, sIdx)}
                category={navItem.label}
              />
            ))}
          </div>
        </div>

        {/* ── PANE 2: L2 links ── fixed 320px, same white bg as Pane 1 ─── */}
        <div className="flex-none px-6 py-6" style={{ width: '320px' }}>
          {activeSectionIndex !== null && (
            <div className="flex flex-col" id={`main-l2-pane-${colIdx}-${activeSectionIndex}`}>
              {sections[activeSectionIndex]?.children
                ?.filter((link) => !link.disablePageLinkInMegaMenu)
                .map((link, l2Idx, filteredLinks) => {
                  const isFirst = l2Idx === 0
                  const isLast = l2Idx === filteredLinks.length - 1
                  return (
                    <a
                      id={`main-l2-${colIdx}-${activeSectionIndex}-${l2Idx}`}
                      key={`l2-${l2Idx}`}
                      href={link.url}
                      target={link.target ?? '_self'}
                      className="flex items-start gap-4 p-3 rounded-lg hover:bg-(--color-button-text-hover-bg) transition-colors"
                      tabIndex={isOpen ? 0 : -1}
                      onKeyDown={(e) => {
                        // BACKWARD: go back to the L1 toggle that opened this pane
                        if (e.key === 'Tab' && e.shiftKey && isFirst) {
                          e.preventDefault()
                          const toggle = document.getElementById(`main-l1-${colIdx}-${activeSectionIndex}-toggle`)
                          if (toggle) toggle.focus()
                        }
                        // FORWARD: last L2 → next L1 or CTA
                        if (e.key === 'Tab' && !e.shiftKey && isLast) {
                          const nextL1 = document.getElementById(`main-l1-${colIdx}-${activeSectionIndex + 1}-link`)
                          if (nextL1) {
                            e.preventDefault()
                            nextL1.focus()
                            return
                          }
                          const cta = document.getElementById(`main-cta-${colIdx}`)
                          if (cta) {
                            e.preventDefault()
                            cta.focus()
                          }
                        }
                      }}
                    >
                      <div className="flex flex-col text-left">
                        <span className="text-base font-bold !text-(--sb-mega-menu-title-color) leading-6">
                          {link.label}
                        </span>
                      </div>
                    </a>
                  )
                })}
            </div>
          )}
        </div>

        {/* ── PANE 3: Image CTA ── fixed 260px, themed background ──────── */}
        <div className="flex-none bg-(--sb-mega-menu-img-cta-bg) px-4 py-6" style={{ width: '260px' }}>
          {navItem.image?.src &&
            (navItem.url && !navItem.disablePageLinkInMegaMenu ? (
              <a
                id={`main-cta-${colIdx}`}
                href={navItem.url}
                target={navItem.target ?? '_self'}
                className="block rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-(--sb-focus-ring-color)"
                tabIndex={isOpen ? 0 : -1}
                onKeyDown={handleCTAKeyDown}
              >
                <img src={navItem.image.src} alt={navItem.image.alt || ''} className="w-full h-auto rounded-lg" />
              </a>
            ) : (
              <img src={navItem.image.src} alt={navItem.image.alt || ''} className="w-full h-auto rounded-lg" />
            ))}
        </div>
      </div>
    </li>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// MOBILE ▸ MobileNavSection
// A single L1 item inside the mobile accordion.
// Mirrors MobileMegaMenuSection: centred link + optional inline expand button,
// then a collapsible list of L2 links below.
// ─────────────────────────────────────────────────────────────────────────────
const MobileNavSection = ({ item, active, category }) => {
  const hasL2 = item.children?.filter((c) => !c.disablePageLinkInMegaMenu).length > 0
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    if (!active) setIsExpanded(false)
  }, [active])

  return (
    <div className="w-full">
      <div className="flex items-center justify-center relative w-full px-6 py-3">
        {!item.disablePageLinkInMegaMenu ? (
          <a
            href={item.url}
            target={item.target ?? '_self'}
            className="text-lg font-medium text-(--color-content-primary) transition-colors focus:outline-none focus:ring-2 focus:ring-(--sb-focus-ring-color) rounded"
            tabIndex={active ? 0 : -1}
            onClick={() => {
              dataLayerPush({
                event: 'navigation_menu_click',
                category: category,
                click_text: item.label,
              })
            }}
          >
            {item.label}
          </a>
        ) : (
          <span className="text-lg font-medium text-(--color-content-primary)">{item.label}</span>
        )}

        {hasL2 && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setIsExpanded(!isExpanded)
            }}
            className="absolute right-0 top-0 bottom-0 w-16 flex items-center justify-center z-10 text-(--sb-mega-menu-toggle-icon-color) focus:outline-none focus:ring-2 focus:ring-(--sb-focus-ring-color)"
            aria-expanded={isExpanded}
            tabIndex={active ? 0 : -1}
          >
            <span className="sr-only">Toggle submenu for {item.label}</span>
            <ChevronDownIcon
              className={`h-5 w-5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>

      {/* L2 links — slide open/closed */}
      {hasL2 && (
        <ul
          className={`
          flex flex-col items-center w-full overflow-hidden transition-all duration-300
          ${isExpanded ? 'max-h-[3000px] opacity-100 visible py-2' : 'max-h-0 opacity-0 invisible'}
        `}
        >
          {item.children
            .filter((c) => !c.disablePageLinkInMegaMenu)
            .map((link, idx) => (
              <li key={`l2-${idx}`} className="w-full text-center">
                <a
                  href={link.url}
                  target={link.target ?? '_self'}
                  className="block py-2.5 px-6 text-base font-normal text-(--sb-mega-menu-list-text) transition-colors focus:outline-none focus:ring-2 focus:ring-(--sb-focus-ring-color) rounded"
                  tabIndex={active && isExpanded ? 0 : -1}
                  onClick={() => {
                    dataLayerPush({
                      event: 'navigation_menu_click',
                      category: category,
                      click_text: item.label,
                    })
                  }}
                >
                  {link.label}
                </a>
              </li>
            ))}
        </ul>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// MOBILE ▸ MobileNavColumn
// A full top-level item with an expandable accordion panel.
// Mirrors MobileMegaMenuColumn: centred large text, right-side touch chevron,
// expanding panel of MobileNavSection items + optional CTA image.
// ─────────────────────────────────────────────────────────────────────────────
const MobileNavColumn = ({ navItem, colIdx, isOpen, onToggle }) => {
  const { detectPaletteClass } = useContext(MarkupContext)
  const hasChildLinks = navItem.children?.length > 0

  return (
    <li className="w-full">
      <div className="flex items-center justify-center relative w-full px-6 py-4">
        {!navItem.disablePageLinkInMegaMenu ? (
          <>
            <a
              href={navItem.url || '#'}
              target={navItem.target ?? '_self'}
              className={detectPaletteClass(`
                text-2xl font-normal text-(--color-slate-600) transition-colors
                focus:outline-none focus:ring-2 focus:ring-(--sb-focus-ring-color) rounded leading-8
                ${isOpen ? 'text-primary-500' : ''}
              `)}
              aria-haspopup={hasChildLinks ? 'true' : 'false'}
              aria-expanded={hasChildLinks ? (isOpen ? 'true' : 'false') : undefined}
              onClick={() => {
                dataLayerPush({
                  event: 'navigation_menu_click',
                  category: navItem.label,
                  click_text: navItem.label,
                })
              }}
            >
              {navItem.label}
            </a>

            {hasChildLinks && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onToggle(colIdx)
                }}
                className="absolute right-0 top-0 bottom-0 w-16 flex items-center justify-center z-10 text-(--sb-mega-menu-list-text) focus:outline-none focus:ring-2 focus:ring-(--sb-focus-ring-color)"
              >
                <span className="sr-only">Expand menu for {navItem.label}</span>
                <ChevronDownIcon
                  className={`h-5 w-5 transition-transform duration-300 ${isOpen ? 'rotate-180 text-primary-500' : ''}`}
                />
              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={() => {
              onToggle(colIdx)
              dataLayerPush({
                event: 'navigation_menu_click',
                category: navItem.label,
                click_text: navItem.label,
              })
            }}
            className={detectPaletteClass(`
              w-full flex items-center justify-center text-2xl font-normal
              text-(--color-slate-600) transition-colors focus:outline-none focus:ring-2 focus:ring-(--sb-focus-ring-color)
              ${isOpen ? 'text-primary-500' : ''}
            `)}
            aria-haspopup="true"
            aria-expanded={isOpen ? 'true' : 'false'}
          >
            <span>{navItem.label}</span>
            <ChevronDownIcon
              className={`absolute right-6 h-5 w-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>

      {/* Expandable accordion panel */}
      <div
        className={`
        flex flex-col items-center w-full overflow-hidden transition-all duration-300
        ${isOpen ? 'max-h-[3000px] opacity-100 visible py-2' : 'max-h-0 opacity-0 invisible'}
      `}
      >
        {navItem.children?.map((child, sIdx) => (
          <MobileNavSection key={`mobile-section-${sIdx}`} item={child} active={isOpen} category={navItem.label} />
        ))}

        {/* Optional CTA image (navItem.image → mirrors MobileMegaMenuNav CTA section) */}
        {navItem.image?.src && (
          <div className="p-6 w-full max-w-sm mx-auto">
            {navItem.url && !navItem.disablePageLinkInMegaMenu ? (
              <a
                href={navItem.url}
                target={navItem.target ?? '_self'}
                className="block focus:outline-none focus:ring-2 focus:ring-(--sb-focus-ring-color) rounded-lg"
                tabIndex={isOpen ? 0 : -1}
              >
                <img
                  src={navItem.image.src}
                  alt={navItem.image.alt || ''}
                  className="w-full h-auto rounded-lg shadow-sm"
                />
              </a>
            ) : (
              <img
                src={navItem.image.src}
                alt={navItem.image.alt || ''}
                className="w-full h-auto rounded-lg shadow-sm"
              />
            )}
          </div>
        )}
      </div>
    </li>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// MainNav  (root)
// Renders as either the desktop mega-menu nav or the mobile accordion nav
// based on props.type ('desktop' | 'mobile').
// ─────────────────────────────────────────────────────────────────────────────
const MainNav = (props) => {
  const [activeIndex, setActiveIndex] = useState(null)
  const isMountedRef = useRef(false)
  const isDesktop = props.type === 'desktop'

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Toggle a column open/closed and sync global menu state + close search/ctas
  const handleToggle = (idx) => {
    setActiveIndex((prev) => {
      const newActive = prev === idx ? null : idx
      const isActive = newActive !== null

      props.setMenuActive?.(isActive)
      if (isActive) {
        // Close search and utility CTAs when the nav opens
        props.setSearchActive?.(false)
        props.setCtasActive?.(false)
      }
      return newActive
    })
  }

  // Close our panel when an external actor (search, cta, etc.) closes the menu
  useEffect(() => {
    if (props.menuActive === false) {
      setActiveIndex(null)
    }
  }, [props.menuActive])

  // Escape key — close and restore focus
  useEffect(() => {
    const handler = (evt) => {
      if (evt.key !== 'Escape') return
      setActiveIndex(null)
      props.setMenuActive?.(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  // Outside-click — desktop only, with proper cleanup
  useEffect(() => {
    if (!isDesktop || activeIndex === null || !isMountedRef.current) return

    const handler = (evt) => {
      if (!isMountedRef.current) return
      if (!evt.target.closest('[data-type="desktop-navigation"]')) {
        setActiveIndex(null)
        props.setMenuActive?.(false)
      }
    }

    const id = setTimeout(() => {
      if (isMountedRef.current) document.addEventListener('click', handler, true)
    }, 150)

    return () => {
      clearTimeout(id)
      document.removeEventListener('click', handler, true)
    }
  }, [isDesktop, activeIndex])

  // ── Desktop render ───────────────────────────────────────────────────────
  if (isDesktop) {
    return (
      <nav data-type="desktop-navigation" className="hidden xl:!block h-full relative">
        <ul data-type="menu-level-0" className="flex items-center h-full">
          {props.nav?.map((navItem, idx) => {
            // Items with children → full mega-menu column
            if (navItem.children?.length) {
              return (
                <DesktopNavColumn
                  key={`col-${idx}`}
                  navItem={navItem}
                  colIdx={idx}
                  isOpen={activeIndex === idx}
                  onToggle={handleToggle}
                />
              )
            }
            // Leaf items (no children) — plain nav link
            if (navItem.disablePageLinkInMegaMenu) return <span key={idx} />
            return (
              <li key={`leaf-${idx}`} data-level="0">
                <a
                  href={navItem.url}
                  target={navItem.target ?? '_self'}
                  style={{
                    '--nav-decor': `var(--color-${themeConfig.settings.nav?.desktop?.highlight || 'primary'}-500, var(--sb-nav-decor-color))`,
                  }}
                  className="h-full xl:px-1.5 2xl:px-3 py-3 flex items-center whitespace-nowrap font-normal !text-(--sb-mega-menu-title-color) hover:underline hover:decoration-4 hover:decoration-(--nav-decor)   focus:-outline-offset-4"
                >
                  {navItem.label}
                </a>
              </li>
            )
          })}
        </ul>
      </nav>
    )
  }

  // ── Mobile render ────────────────────────────────────────────────────────
  return (
    <nav data-type="mobile-navigation" className="w-full">
      <ul className="flex flex-col items-center w-full gap-2">
        {props.nav?.map((navItem, idx) => {
          // Items with children → accordion column
          if (navItem.children?.length) {
            return (
              <MobileNavColumn
                key={`mobile-col-${idx}`}
                navItem={navItem}
                colIdx={idx}
                isOpen={activeIndex === idx}
                onToggle={handleToggle}
              />
            )
          }
          // Leaf items
          if (navItem.disablePageLinkInMegaMenu) return <span key={idx} />
          return (
            <li key={`mobile-leaf-${idx}`} className="w-full text-center">
              <a
                href={navItem.url}
                target={navItem.target ?? '_self'}
                className="block py-4 px-6 text-2xl font-normal text-(--color-slate-600) transition-colors focus:outline-none focus:ring-2 focus:ring-(--sb-focus-ring-color) rounded"
              >
                {navItem.label}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

export default MainNav
